/**
 * REMUS Web - Design tokens (TypeScript mirror).
 *
 * These constants mirror the CSS custom properties declared in
 * `frontend/src/styles/design-system.less`. They exist so that
 *   - `theme.ts` (MUI) can derive its palette without re-typing hex,
 *   - component-level style props can read the same brand value
 *     without falling back to `var(...)` strings (which MUI's `sx`
 *     and inline `style` accept but lose type-safety on).
 *
 * When you add or change a token here, mirror the same change in
 * `design-system.less`. The two stay in sync by convention; review
 * catches drift.
 *
 * Domain palettes (Highcharts series, heatmap colour stops, synoptic
 * element colours) intentionally live in their own constant files —
 * they are NOT branding and must not be added here.
 */

export const palette = {
  // Surfaces
  bg: '#f7f8fa',
  surface: '#ffffff',
  surfaceSoft: '#f8fafc',
  surfaceHover: '#f1f5f9',
  surfaceMuted: '#f4f5f7',
  surfaceSunken: '#eef1f5',
  surfaceChecker: '#eeeeee',
  surfaceAlt: '#fafafa',

  // Borders
  borderSoft: '#eef1f5',
  border: '#e2e8f0',
  borderStrong: '#cbd5e1',
  borderMuted: '#e0e0e0',

  // Text
  text: '#0f172a',
  textDeep: '#1f2733',
  textSoft: '#334155',
  textMute: '#475569',
  muted: '#64748b',
  mutedAlt: '#6b7280',
  disabled: '#94a3b8',
  onAccent: '#ffffff',
  onDark: '#ffffff',

  // Brand
  brandNavy: '#102241',
  brandNavySoft: '#2c4570',
  brandNavyDeep: '#0a1628',

  // Primary accent (deep blue)
  accent: '#1e3a8a',
  accentHover: '#1d4ed8',
  accentSoft: '#e0e7ff',
  accentBorder: '#c7d2fe',
  accentDeep: '#2563eb',
  // Highlight text accent (light mode reuses the deep accent)
  accentText: '#1e3a8a',

  // Semantic - success
  successSoft: '#ecfdf5',
  successLight: '#22c55e',
  success: '#047857',
  successMain: '#16a34a',
  successDark: '#15803d',
  successDarker: '#065f46',

  // Semantic - info
  infoSoft: '#e0f2fe',
  infoLight: '#0ea5e9',
  info: '#0284c7',
  infoMain: '#0284c7',
  infoDark: '#0369a1',
  infoAlt: '#2185d0',

  // Semantic - warning
  warningSoft: '#fffbeb',
  warningLight: '#f97316',
  warning: '#b45309',
  warningMain: '#ea580c',
  warningDark: '#c2410c',
  warningAmber: '#fcd34d',
  warningDeep: '#ff8000',

  // Semantic - danger
  dangerSoft: '#fee2e2',
  dangerSofter: '#fef2f2',
  dangerTint: '#fecaca',
  dangerLight: '#ef4444',
  danger: '#b91c1c',
  dangerMain: '#dc2626',
  dangerDark: '#991b1b',

  // Hover / row tints
  rowHover: '#eff6ff',
} as const;

export type Palette = typeof palette;
export type PaletteKey = keyof Palette;

/**
 * Dark-mode palette — same keys as `palette`, retuned for a low-glare
 * "control-room" dark surface. Mirrors the `html[data-theme='dark']`
 * overrides in `design-system.less`; KEEP IN SYNC.
 *
 * Most of the app reads colours through `var(--ds-*)` (which the LESS
 * dark block flips automatically), so this object exists only so that
 * `theme.ts` can build a concrete MUI palette for the dark mode — MUI
 * needs real hex to compute contrast/ripple/disabled shades, it cannot
 * resolve `var(...)` at theme-construction time.
 */
export const darkPalette: Record<PaletteKey, string> = {
  // Surfaces — a deep navy-slate ramp (sunken → hover), tinted toward the
  // brand navy header (#102241) so the dark chrome is one cohesive family.
  bg: '#1e2436',
  surface: '#252e45',
  surfaceSoft: '#2b3550',
  surfaceHover: '#34405e',
  surfaceMuted: '#28324c',
  surfaceSunken: '#161b2b',
  surfaceChecker: '#2b3550',
  surfaceAlt: '#2b3550',

  // Borders (navy-tinted)
  borderSoft: '#2d3a55',
  border: '#3c4a6a',
  borderStrong: '#4f5f85',
  borderMuted: '#343f5c',

  // Text (Dracula foreground #f8f8f2)
  text: '#f8f8f2',
  textDeep: '#ffffff',
  textSoft: '#dcdde6',
  textMute: '#b6b9cd',
  muted: '#8b90b4',
  mutedAlt: '#898eb0',
  disabled: '#6272a4',
  onAccent: '#ffffff',
  onDark: '#ffffff',

  // Brand — aligned to the navy header family
  brandNavy: '#15213b',
  brandNavySoft: '#324269',
  brandNavyDeep: '#0d1626',

  // Primary accent
  accent: '#3b82f6',
  accentHover: '#60a5fa',
  accentSoft: '#2c3a60',
  accentBorder: '#42537e',
  accentDeep: '#60a5fa',
  // Electric cyan for highlight text (links, active tabs, selected items)
  accentText: '#65bdf7',

  // Semantic - success (main kept deep enough for white button text;
  // soft tint lightened to sit on the lighter surface)
  successSoft: '#1e3a2c',
  successLight: '#50fa7b',
  success: '#22c55e',
  successMain: '#16a34a',
  successDark: '#15803d',
  successDarker: '#134e30',

  // Semantic - info
  infoSoft: '#1d3a4a',
  infoLight: '#8be9fd',
  info: '#38bdf8',
  infoMain: '#38bdf8',
  infoDark: '#0ea5e9',
  infoAlt: '#8be9fd',

  // Semantic - warning
  warningSoft: '#3a3220',
  warningLight: '#ffb86c',
  warning: '#ffb86c',
  warningMain: '#fb923c',
  warningDark: '#f59e0b',
  warningAmber: '#f1fa8c',
  warningDeep: '#ff9d2e',

  // Semantic - danger
  dangerSoft: '#45272f',
  dangerSofter: '#36242a',
  dangerTint: '#7f3a3a',
  dangerLight: '#ff7b7b',
  danger: '#ff6e6e',
  dangerMain: '#ef4444',
  dangerDark: '#dc2626',

  // Hover / row tints
  rowHover: '#28344e',
};

/** A UI colour mode. */
export type ThemeMode = 'light' | 'dark';

/** Concrete palette for a given mode (used by the MUI theme factory). */
export const paletteFor = (mode: ThemeMode): Record<PaletteKey, string> =>
  mode === 'dark' ? darkPalette : palette;

/** Geometry tokens (mirror of --ds-radius-* in design-system.less). */
export const radius = {
  sm: 4,
  md: 6,
  lg: 8,
} as const;

/** Shadow tokens (mirror of --ds-shadow-* in design-system.less). */
export const shadow = {
  s1: '0 1px 2px rgba(15, 23, 42, 0.06)',
  s2: '0 4px 16px rgba(15, 23, 42, 0.08)',
  s3: '0 6px 24px rgba(15, 23, 42, 0.12)',
} as const;

/**
 * CSS-variable accessor — preferred when writing inline `sx={{...}}`
 * or `style={{...}}` props in components that consume the design
 * system. Using `cssVar('accent')` instead of `palette.accent`
 * gives runtime themability (a future dark mode flips the variable,
 * not every component). Use the raw `palette` constant only where
 * a non-CSS context needs the hex (e.g. canvas drawing, SVG fill
 * attributes that don't accept `var()`).
 */
export const cssVar = (key: PaletteKey): string => {
  const map: Record<PaletteKey, string> = {
    bg: 'var(--ds-bg)',
    surface: 'var(--ds-surface)',
    surfaceSoft: 'var(--ds-surface-soft)',
    surfaceHover: 'var(--ds-surface-hover)',
    surfaceMuted: 'var(--ds-surface-muted)',
    surfaceSunken: 'var(--ds-surface-sunken)',
    surfaceChecker: 'var(--ds-surface-checker)',
    surfaceAlt: 'var(--ds-surface-alt)',
    borderSoft: 'var(--ds-border-soft)',
    border: 'var(--ds-border)',
    borderStrong: 'var(--ds-border-strong)',
    borderMuted: 'var(--ds-border-muted)',
    text: 'var(--ds-text)',
    textDeep: 'var(--ds-text-deep)',
    textSoft: 'var(--ds-text-soft)',
    textMute: 'var(--ds-text-mute)',
    muted: 'var(--ds-muted)',
    mutedAlt: 'var(--ds-muted-alt)',
    disabled: 'var(--ds-disabled)',
    onAccent: 'var(--ds-on-accent)',
    onDark: 'var(--ds-on-dark)',
    brandNavy: 'var(--ds-brand-navy)',
    brandNavySoft: 'var(--ds-brand-navy-soft)',
    brandNavyDeep: 'var(--ds-brand-navy-deep)',
    accent: 'var(--ds-accent)',
    accentHover: 'var(--ds-accent-hover)',
    accentSoft: 'var(--ds-accent-soft)',
    accentBorder: 'var(--ds-accent-border)',
    accentDeep: 'var(--ds-accent-deep)',
    accentText: 'var(--ds-accent-text)',
    successSoft: 'var(--ds-success-soft)',
    successLight: 'var(--ds-success-light)',
    success: 'var(--ds-success)',
    successMain: 'var(--ds-success-main)',
    successDark: 'var(--ds-success-dark)',
    successDarker: 'var(--ds-success-darker)',
    infoSoft: 'var(--ds-info-soft)',
    infoLight: 'var(--ds-info-light)',
    info: 'var(--ds-info)',
    infoMain: 'var(--ds-info-main)',
    infoDark: 'var(--ds-info-dark)',
    infoAlt: 'var(--ds-info-alt)',
    warningSoft: 'var(--ds-warning-soft)',
    warningLight: 'var(--ds-warning-light)',
    warning: 'var(--ds-warning)',
    warningMain: 'var(--ds-warning-main)',
    warningDark: 'var(--ds-warning-dark)',
    warningAmber: 'var(--ds-warning-amber)',
    warningDeep: 'var(--ds-warning-deep)',
    dangerSoft: 'var(--ds-danger-soft)',
    dangerSofter: 'var(--ds-danger-softer)',
    dangerTint: 'var(--ds-danger-tint)',
    dangerLight: 'var(--ds-danger-light)',
    danger: 'var(--ds-danger)',
    dangerMain: 'var(--ds-danger-main)',
    dangerDark: 'var(--ds-danger-dark)',
    rowHover: 'var(--ds-row-hover)',
  };
  return map[key];
};
